/**
 * ProgrammeSelect.js
 * Reusable dropdown for MoH programme selection.
 * Loads from moh_programmes table via fleet/programmes API.
 * 
 * Usage:
 *   import ProgrammeSelect from '../fleet/ProgrammeSelect';
 *   <ProgrammeSelect value={data.assigned_programme}
 *                    onChange={v => handleChange(v, 'assigned_programme')} />
 */
import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import FleetService from '../../services/FleetService';

const { Option } = Select;

let _cache = null; // module-level cache so we only fetch once per session

export default function ProgrammeSelect({
    value,
    onChange,
    placeholder = 'Select programme',
    allowClear  = true,
    disabled    = false,
    style       = {},
    showCode    = true,  // show abbreviation badge alongside name
}) {
    const [programmes, setProgrammes] = useState(_cache || []);
    const [loading,    setLoading]    = useState(!_cache);

    useEffect(() => {
        if (_cache) return;
        FleetService.getProgrammes().then(res => {
            if (Number(res?.data?.code) === 200) {
                _cache = res.data.result || [];
                setProgrammes(_cache);
            }
        }).catch(console.error)
          .finally(() => setLoading(false));
    }, []);

    return (
        <Select
            showSearch
            allowClear={allowClear}
            disabled={disabled}
            loading={loading}
            placeholder={placeholder}
            value={value || undefined}
            onChange={onChange}
            style={{ width: '100%', ...style }}
            filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            optionLabelProp="label"
        >
            {programmes.map(p => (
                <Option key={p.id} value={p.name} label={`${p.code} — ${p.name}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            fontSize: 10, fontWeight: 500,
                            padding: '1px 6px', borderRadius: 4, flexShrink: 0,
                            background: '#f0f5ff', border: '1px solid #adc6ff',
                            color: '#1d4ed8', minWidth: 36, textAlign: 'center',
                        }}>
                            {p.code}
                        </span>
                        <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                            {p.name}
                        </span>
                    </div>
                </Option>
            ))}
        </Select>
    );
}

// Named export for use in filter bars where you just need the options array
export function useProgrammes() {
    const [programmes, setProgrammes] = useState(_cache || []);
    useEffect(() => {
        if (_cache) { setProgrammes(_cache); return; }
        FleetService.getProgrammes().then(res => {
            if (Number(res?.data?.code) === 200) {
                _cache = res.data.result || [];
                setProgrammes(_cache);
            }
        }).catch(console.error);
    }, []);
    return programmes;
}